// Phase 2 §4: Projection Comparison Drawer
// Side-by-side comparison of Legal vs Projection ownership

import { useState, useEffect } from 'react';
import { Download, Shield, Sparkles, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ProtectedAction } from '@/components/auth/ProtectedAction';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

interface ProjectionComparisonDrawerProps {
  companyId: string;
  scenarioId?: string;
  trigger?: React.ReactNode;
}

interface ComparisonRow {
  shareholder: string;
  legalPercent: number;
  projectionPercent: number;
  delta: number;
}

export function ProjectionComparisonDrawer({
  companyId,
  scenarioId,
  trigger,
}: ProjectionComparisonDrawerProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [comparison, setComparison] = useState<ComparisonRow[]>([]);

  useEffect(() => {
    if (open && companyId) {
      loadComparison();
    }
  }, [open, companyId, scenarioId]);

  async function loadComparison() {
    try {
      setLoading(true);

      // Fetch legal cap table data
      const { data: legalData, error: legalError } = await supabase
        .from('cap_table_summary')
        .select('*')
        .eq('company_id', companyId);

      if (legalError) throw legalError;

      // Fetch projection data if scenario provided
      let projectionData: any[] = [];
      if (scenarioId) {
        const { data: snapshot, error: snapshotError } = await supabase
          .from('cap_table_snapshots')
          .select('snapshot')
          .eq('id', scenarioId)
          .single();

        if (!snapshotError && snapshot) {
          projectionData = snapshot.snapshot as any[];
        }
      }

      // Build comparison rows
      const shareholderMap = new Map<string, ComparisonRow>();

      // Process legal data
      legalData?.forEach((entry: any) => {
        const name = entry.shareholder_name || 'Unknown';
        const pct = (entry.total_shares / getTotalShares(legalData)) * 100;

        shareholderMap.set(name, {
          shareholder: name,
          legalPercent: pct,
          projectionPercent: 0,
          delta: 0,
        });
      });

      // Process projection data
      projectionData.forEach((entry: any) => {
        const name = entry.holder || entry.shareholder || 'Unknown';
        const pct = entry.pct || 0;

        const existing = shareholderMap.get(name);
        if (existing) {
          existing.projectionPercent = pct;
          existing.delta = pct - existing.legalPercent;
        } else {
          shareholderMap.set(name, {
            shareholder: name,
            legalPercent: 0,
            projectionPercent: pct,
            delta: pct,
          });
        }
      });

      setComparison(Array.from(shareholderMap.values()));
    } catch (error: any) {
      console.error('Error loading comparison:', error);
      toast.error('Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  }

  function getTotalShares(data: any[]): number {
    return data.reduce((sum, entry) => sum + (entry.total_shares || 0), 0);
  }

  function formatPercent(value: number): string {
    return `${value.toFixed(2)}%`;
  }

  function getDeltaIcon(delta: number) {
    if (delta > 0.01) return <TrendingUp className="h-4 w-4 text-success" />;
    if (delta < -0.01) return <TrendingDown className="h-4 w-4 text-destructive" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  }

  function getDeltaColor(delta: number): string {
    if (delta > 0.01) return 'text-success';
    if (delta < -0.01) return 'text-destructive';
    return 'text-muted-foreground';
  }

  async function handleExportPDF() {
    toast.info('PDF export coming soon');
  }

  async function handleConvertToLegal() {
    toast.info('Convert to Legal coming soon - requires admin approval workflow');
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            Compare to Legal
          </Button>
        )}
      </SheetTrigger>
      <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Projection vs Legal Comparison</SheetTitle>
          <SheetDescription>Side-by-side ownership analysis</SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-success" />
                <span className="text-sm font-medium">Legal</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-warning" />
                <span className="text-sm font-medium">Projection</span>
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Shareholder</TableHead>
                  <TableHead className="text-right">Legal %</TableHead>
                  <TableHead className="text-right">Proj %</TableHead>
                  <TableHead className="text-right">Δ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparison.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{row.shareholder}</TableCell>
                    <TableCell className="text-right">{formatPercent(row.legalPercent)}</TableCell>
                    <TableCell className="text-right">
                      {formatPercent(row.projectionPercent)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {getDeltaIcon(row.delta)}
                        <span className={getDeltaColor(row.delta)}>
                          {row.delta > 0 ? '+' : ''}
                          {formatPercent(row.delta)}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex items-center gap-2 pt-4 border-t">
              <Button variant="outline" size="sm" onClick={handleExportPDF} className="flex-1">
                <Download className="h-4 w-4 mr-2" />
                Export PDF
              </Button>

              <ProtectedAction
                allowedRoles={['owner', 'admin']}
                companyId={companyId}
                fallback={
                  <Badge variant="outline" className="flex-1 py-2">
                    Admin Only
                  </Badge>
                }
              >
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleConvertToLegal}
                  className="flex-1"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Convert to Legal
                </Button>
              </ProtectedAction>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
